/*
 * Copyright 2026 Sony Corporation
 */

import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';

// Registers the glTF plugin with the loader.
import '@babylonjs/loaders/glTF/2.0';

import { fetchCached } from '../services/asset-cache';
import { fetchGltf, type Resolution } from '../services/polyhaven';

/**
 * Turning a Poly Haven reference into geometry.
 *
 * The `.gltf` names its buffer and images by relative path, but Poly Haven
 * serves them under a *different* prefix — resolving them relatively 404s. So
 * the API's `include` map is not merely the allowlist devplan §5.5 asks for, it
 * is the only way to find the files at all.
 *
 * Each one is fetched, given an object URL, and written into a copy of the
 * `.gltf`. Anything the asset asks for that the API did not declare has no URL
 * to be rewritten to, so it cannot be fetched — containment by construction
 * rather than by check.
 */

const MAX_BYTES = 40 * 1024 * 1024;

interface Rewritten {
  /** The patched `.gltf`, as something Babylon can be handed. */
  file: File;
  release: () => void;
}

const rewrite = async (gltfUrl: string, include: Record<string, string>, signal: AbortSignal): Promise<Rewritten> => {
  const response = await fetch(gltfUrl, { signal });
  if (!response.ok) {
    throw new Error(`The model could not be downloaded (${response.status}).`);
  }
  const document = (await response.json()) as {
    buffers?: { uri?: string }[];
    images?: { uri?: string }[];
  };

  const listed = [...(document.buffers ?? []), ...(document.images ?? [])].filter(
    (entry): entry is { uri: string } => entry.uri !== undefined && !entry.uri.startsWith('data:')
  );
  // Every reference is checked before anything is fetched, so an unlisted one costs no download at all.
  for (const { uri } of listed) {
    if (!include[uri]) {
      throw new Error(`The model asks for ${uri}, which Poly Haven did not list.`);
    }
  }

  const urls: string[] = [];
  let bytes = 0;

  // Once per file, however many entries name it.
  const downloads = new Map<string, Promise<string>>();
  const resolve = (uri: string): Promise<string> => {
    let pending = downloads.get(uri);
    if (!pending) {
      pending = fetchCached(include[uri], signal).then((blob) => {
        bytes += blob.size;
        if (bytes > MAX_BYTES) {
          throw new Error('The model is larger than this editor will load.');
        }
        const url = URL.createObjectURL(blob);
        urls.push(url);
        return url;
      });
      downloads.set(uri, pending);
    }
    return pending;
  };

  // In parallel, and settled rather than raced: a failure must still wait for
  // the others, or a URL made after the cleanup below would never be revoked.
  const results = await Promise.allSettled(
    listed.map(async (entry) => {
      entry.uri = await resolve(entry.uri);
    })
  );
  const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failed) {
    for (const url of urls) {
      URL.revokeObjectURL(url);
    }
    throw failed.reason;
  }

  return {
    // A File rather than a URL: the extension is how the loader picks its plugin.
    file: new File([JSON.stringify(document)], 'asset.gltf', { type: 'model/gltf+json' }),
    release: () => {
      for (const url of urls) {
        URL.revokeObjectURL(url);
      }
    },
  };
};

/**
 * Loads a model into an unattached container.
 *
 * The caller decides when — and whether — it joins the scene, which is what
 * makes a slow or failed import harmless (`scene-switch.md` §2 rule 5).
 */
export const loadPolyHavenModel = async (
  scene: Scene,
  slug: string,
  resolution: Resolution,
  signal: AbortSignal
): Promise<AssetContainer> => {
  const files = await fetchGltf(slug, resolution, signal);
  if (!files) {
    throw new Error(`${slug} has no glTF at ${resolution}.`);
  }

  const { file, release } = await rewrite(files.url, files.include, signal);
  try {
    return await LoadAssetContainerAsync(file, scene);
  } finally {
    // Babylon has read everything it needs by now; the blobs would otherwise
    // hold the whole asset in memory for the life of the tab.
    release();
  }
};
